import React, { FunctionComponent } from 'react';

import { useParams, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

import Text from 'common/components/Text/Text';
import Loader from 'common/components/Loader/Loader';
import TabsComponent from 'features/explore/country/tabs/Tabs';
import DetailInfo from 'common/components/DetailInfo/DetailInfo';

import { useGetCountryQuery } from 'services/exploreApi';
import { formatIntakeDate, formatNumber } from 'common/utils/conversions';

import styles from './CountryDetail.module.scss';
import Placeholder from 'assets/explore/placeholder.png';
import { ReactComponent as MoneyIcon } from 'assets/icons/money.svg';
import { ReactComponent as Language } from 'assets/explore/language.svg';

const CountryDetail: FunctionComponent = () => {
  const { countrySlug } = useParams<{ countrySlug: string }>();
  const [countryId] = countrySlug?.split('--') || [];
  const { data: country, isLoading } = useGetCountryQuery(parseInt(countryId || ''));
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  return (
    <div>
      {isLoading ? (
        <Loader />
      ) : (
        <div>
          <div className={styles.desktopContainer}>
            {isMobile && (
              <div className={styles.headerContainer}>
                <div className="row">
                  <a className={styles.back} onClick={() => navigate(-1)}>
                    <i className={`bi bi-chevron-left ${styles.vector}`}></i>
                  </a>
                </div>
                <div className="d-flex flex-row justify-content-between mt-4">
                  <div>
                    <Text size={20} weight={700}>
                      {country?.data.name}
                    </Text>
                  </div>
                  {country?.data.priority && country.data.priority > 1000 && (
                    <div className={styles.tagDestination}>
                      <div className="me-1">🔥</div>
                      <div>
                        <Text size={12} weight={600}>
                          hot destination
                        </Text>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {country?.data.photos?.length ? (
              <div
                id="carouselExampleIndicators"
                className={`carousel slide`}
                data-bs-ride="carousel"
              >
                <div className="carousel-indicators">
                  {country.data.photos.length > 1 &&
                    country.data.photos.map((photo: string, index: number) => (
                      <div key={index}>
                        {index === 0 ? (
                          <button
                            type="button"
                            data-bs-target="#carouselExampleIndicators"
                            data-bs-slide-to={`${index}`}
                            className={`active ${styles.buttonDot}`}
                            aria-current="true"
                            aria-label={`Slide ${index}`}
                          ></button>
                        ) : (
                          <button
                            type="button"
                            data-bs-target="#carouselExampleIndicators"
                            data-bs-slide-to={`${index}`}
                            className={`${styles.buttonDot}`}
                            aria-label={`Slide ${index}`}
                          ></button>
                        )}
                      </div>
                    ))}
                </div>
                <div className={`carousel-inner ${styles.carouselContainer}`}>
                  {country.data.photos.length &&
                    country.data.photos.map((photo: string, index: number) => (
                      <div className={`carousel-item ${index === 0 ? 'active' : ''}`} key={index}>
                        <div className="d-flex flex-row justify-content-center">
                          <img
                            src={`${photo}`}
                            className={`${styles.photo}`}
                            alt={`Photo ${index + 1} of ${country.data.name}`}
                          />
                        </div>
                      </div>
                    ))}
                </div>
                {country.data.photos.length > 1 && (
                  <div>
                    <button
                      className={`carousel-control-prev`}
                      type="button"
                      data-bs-target="#carouselExampleIndicators"
                      data-bs-slide="prev"
                    >
                      <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                      <span className="visually-hidden">Previous</span>
                    </button>
                    <button
                      className={`carousel-control-next`}
                      type="button"
                      data-bs-target="#carouselExampleIndicators"
                      data-bs-slide="next"
                    >
                      <span className="carousel-control-next-icon" aria-hidden="true"></span>
                      <span className="visually-hidden">Next</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.placeholderPhoto}>
                <div>
                  <img src={Placeholder} className={`${styles.f}`} alt="placeholder" />
                </div>
              </div>
            )}
            {!isMobile ? (
              <div className="row">
                <div className="col-12">
                  <div className={styles.section}>
                    <div className="d-flex flex-row align-items-center justify-content-between">
                      <div className="d-flex flex-row align-items-center">
                        <Text size={24} weight={700}>
                          {country?.data.name}
                        </Text>
                        {country?.data.priority && country.data.priority > 1000 && (
                          <div className={styles.tagDestination}>
                            <div className="me-2">🔥</div>
                            <div>
                              <Text size={16} weight={600}>
                                hot destination
                              </Text>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className={`${styles.share}`}>
                        {/* <div>
                          <i className="bi bi-heart"></i>
                        </div> */}
                        <div>
                          <i className="bi bi-share"></i>
                        </div>
                      </div>
                    </div>
                    {country?.data.description && (
                      <div className={styles.descriptionCountry}>
                        <Text size={16} inlineStyle={{ textAlign: 'justify' }}>
                          {country?.data.description}
                        </Text>
                      </div>
                    )}
                  </div>
                  <div className={styles.infoContainer}>
                    <div className={`card ${styles.infoCard}`}>
                      <div className={styles.details}>
                        {country?.data?.tuition_fee_min && (
                          <DetailInfo
                            title={`${formatNumber(country.data.tuition_fee_min)}-${formatNumber(
                              country.data.tuition_fee_max
                            )}`}
                            subtitle={`${country.data.currency}/year`}
                            description={'Tuition fee'}
                            icon={<MoneyIcon />}
                            className={styles.detailItem}
                          />
                        )}
                        {country?.data?.cost_of_living_min && (
                          <DetailInfo
                            title={`${formatNumber(country.data.cost_of_living_min)}-${formatNumber(
                              country.data.cost_of_living_max
                            )}`}
                            subtitle={`${country.data.currency}/year`}
                            description={'Average living cost'}
                            icon={<i className="bi bi-cash-coin"></i>}
                            className={styles.detailItem}
                          />
                        )}
                        {country?.data?.next_intakes && (
                          <DetailInfo
                            title={formatIntakeDate(country.data.next_intakes[0])}
                            description={'Intakes'}
                            icon={<i className="bi bi-calendar-week"></i>}
                            className={styles.detailItem}
                          />
                        )}
                        {/* {firstUpperCase(country?.data.language || '')} */}
                        <DetailInfo
                          title={'English'}
                          description={'Language'}
                          icon={<Language />}
                          className={styles.detailItem}
                        />
                        {country?.data.number_international_students && (
                          <DetailInfo
                            title={formatNumber(country.data.number_international_students)}
                            description={'International Student number'}
                            icon={<i className="bi bi-mortarboard"></i>}
                            className={styles.detailItem}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <TabsComponent country={country || undefined} />
                </div>
              </div>
            ) : (
              <div>
                <div className={styles.infoContainer}>
                  <div className={`card ${styles.infoCard}`}>
                    <div className={styles.header}>
                      <div>
                        <Text size={15} weight={700}>
                          Country details
                        </Text>
                      </div>
                      {/* <div>
                      <i className="bi bi-heart"></i>
                    </div> */}
                    </div>
                    <div className={styles.details}>
                      {country?.data?.tuition_fee_avg && (
                        <DetailInfo
                          title={`${formatNumber(country.data.tuition_fee_avg)}`}
                          subtitle={`${country.data.currency}/year`}
                          description={'Tuition fee'}
                          icon={<MoneyIcon />}
                          className={styles.detailItem}
                        />
                      )}
                      {country?.data?.cost_of_living_avg && (
                        <DetailInfo
                          title={`${formatNumber(country.data.cost_of_living_avg)}`}
                          description={`${country.data.currency}/year`}
                          icon={<i className="bi bi-cash-coin"></i>}
                          className={styles.detailItem}
                        />
                      )}
                      {country?.data?.next_intakes && (
                        <DetailInfo
                          title={formatIntakeDate(country.data.next_intakes[0])}
                          description={'Intakes'}
                          icon={<i className="bi bi-calendar-week"></i>}
                          className={styles.detailItem}
                        />
                      )}
                      {/* {firstUpperCase(country?.data.language || '')} */}
                      <DetailInfo
                        title={'English'}
                        description={'Language'}
                        icon={<Language />}
                        className={styles.detailItem}
                      />
                      {country?.data.number_international_students && (
                        <DetailInfo
                          title={formatNumber(country.data.number_international_students)}
                          description={'International Student number'}
                          icon={<i className="bi bi-mortarboard"></i>}
                          className={styles.detailItem}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <TabsComponent country={country || undefined} />
                <div style={{ paddingTop: 50 }}></div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryDetail;
